import { SelectedProductData } from "common/components/ProductChooser";
import { useFeatureOn, FeatureName } from "common/Split";
import { clamp, includes, isEmpty, isInteger, isNil, map, some, values, mapValues } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  transferCreateAddProduct,
  transferCreateRemoveProduct,
  transferCreateSetIsEachPickingOrderFlag,
  transferCreateUpdateProductLotNumber,
  transferCreateUpdateProductNumberOfCases,
  transferCreateUpdateProductQty,
} from "transfers/create/actions";
import { CreateTransferLoader } from "transfers/create/CreateTransferLoader.types";
import { addKittedProduct } from "transfers/create/store/addKittedProduct";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { getReplenishmentUnifiedInventory } from "./replenishment/warehouse-selection/actions/getReplenishmentUnifiedInventory";
import { DskuQty, InventoryPool } from "@deliverr/business-types";
import { getProductChooserKittedDSKUDetails } from "common/components/ProductChooser/ProductChooserSelector";
import { ProductListItem } from "inventory/list/InventoryListReducer";
import { removeKittedProduct } from "transfers/create/store/removeKittedProduct";
import { useCallback, useMemo, useState } from "react";
import {
  DistributionChannel,
  InvalidEntityData,
  InvalidEntityType,
  InvalidEntityIssueType,
} from "@deliverr/replenishment-client";
import { ReplenishmentWarehouseSelectionState } from "transfers/create/store";
import { getSelectedProductsLotNumbersList } from "./utils/getSelectedProductsLotNumbersList";
import { selectAnyIsLoading } from "common/store/selectors/selectAnyIsLoading";
import { ReplenishmentConstants } from "transfers/constants/ReplenishmentConstants";
import { useIntl } from "react-intl";
import {
  formatInvalidEntityMessage,
  selectSelectedProductsAndIssuesMap,
} from "./components/invalid-entity/ProductBladeUtils";
import { invalidProductRelatedIssues } from "./actions/utils/invalidOrderItemUtils";

const isNextStepDisabled = (
  isInventoryLoading: boolean,
  selectedProducts: Record<string, SelectedProductData>,
  warehouseSelection: ReplenishmentWarehouseSelectionState
): boolean => {
  return (
    isInventoryLoading ||
    isEmpty(selectedProducts) ||
    isNil(warehouseSelection.selectedWarehouseId) ||
    some(selectedProducts, (val) => !isEmpty(val.errorMessage)) ||
    some(selectedProducts, (product) => !product.qty)
  );
};

const getAvailableInventory = (
  dsku: string,
  inventoryPool: InventoryPool,
  selectedProducts: Record<string, SelectedProductData>
): number => {
  const product = selectedProducts[dsku];
  return (inventoryPool === InventoryPool.STORAGE ? product?.storageOnHandQty : product?.onHandQty) ?? 0;
};

export const useTransferUnifiedProductSelectionStep = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const {
    distributionChannel,
    isEachPickingOrder,
    warehouseSelection,
    selectedProductsLotNumbers,
    replenishmentOrder,
    bulkProductUploadResult,
    selectedProducts,
  } = useSelector(selectTransferCreate);

  const selectedProductsAndIssues = useSelector(selectSelectedProductsAndIssuesMap);

  const selectedProductsFromRedux = useMemo(() => {
    return mapValues(selectedProductsAndIssues, (product) => {
      return {
        ...product,
        errorMessage: product.invalidEntity
          ? formatInvalidEntityMessage(product.invalidEntity, formatMessage)
          : undefined,
      };
    });
  }, [selectedProductsAndIssues, formatMessage]);

  // State for the product details blade
  const [isProductBladeOpen, setIsProductBladeOpen] = useState<boolean>(false);
  const [productForBlade, setProductForBlade] = useState<SelectedProductData | null>(null);
  const [currentProductInvalidEntities, setCurrentProductInvalidEntities] = useState<InvalidEntityData[]>([]);

  // Callbacks for the blade
  const openProductBlade = useCallback(
    (product: SelectedProductData) => {
      setProductForBlade(product);
      if (replenishmentOrder?.invalidEntities && product?.dsku) {
        const productSpecificIssues = replenishmentOrder.invalidEntities.filter(
          (issue) =>
            issue.invalidEntityId === product.lineItemId &&
            issue.invalidEntityType === InvalidEntityType.ORDER_ITEM &&
            invalidProductRelatedIssues.includes(issue.issueType)
        );
        setCurrentProductInvalidEntities(productSpecificIssues);
      } else {
        setCurrentProductInvalidEntities([]);
      }
      setIsProductBladeOpen(true);
    },
    [replenishmentOrder]
  );

  const closeProductBlade = useCallback(() => {
    setProductForBlade(null);
    setIsProductBladeOpen(false);
    setCurrentProductInvalidEntities([]);
  }, []);

  const isInventoryLoading = useSelector(selectAnyIsLoading)(CreateTransferLoader.FetchStorageInventoryLoader);
  const isNextStepLoading = useSelector(selectAnyIsLoading)(CreateTransferLoader.NextStepLoader);
  const dskuQuantities: DskuQty[] = useMemo(
    () => values(selectedProductsFromRedux).map((product) => ({ dsku: product.dsku, qty: product.qty })),
    [selectedProductsFromRedux]
  );

  const isKittingEnabled =
    useFeatureOn(FeatureName.PrepKittingRSToDTCEnabled) &&
    distributionChannel === DistributionChannel.ECOM_VIA_CROSSDOCK;

  const isEachPickingOn = useFeatureOn(FeatureName.WholesaleOrderEachPicking);

  const isDefaultPoolB2BMerchant = useFeatureOn(FeatureName.DefaultPoolB2BMerchant);

  const inventoryPool = isDefaultPoolB2BMerchant ? InventoryPool.DEFAULT : InventoryPool.STORAGE;

  const shouldShowEachPickButton =
    includes(ReplenishmentConstants.SUPPORTED_EACH_PICK_CHANNELS, distributionChannel) && isEachPickingOn;

  const shouldShowLotNumberDropdown = useFeatureOn(FeatureName.ReplenishmentsLotBasedOutbounds) && !isKittingEnabled;

  const shouldUseCasePack = !isEachPickingOrder || isKittingEnabled;

  const addProduct = useCallback(
    (dsku: string, product: SelectedProductData) => {
      const dskuList = [...dskuQuantities, { dsku }];
      const dskus = dskuList.map((item) => item.dsku);

      if (isKittingEnabled && product.isKit && product.caseQty === 1) {
        dispatch(addKittedProduct(product));
      } else {
        const productWithoutInventory = {
          ...product,
          storageOnHandQty: undefined,
          storageUnfilledOrderQty: undefined,
          storageUnavailableQty: undefined,
          onHandQty: 0,
          unfilledOrderQty: 0,
          unavailableQty: 0,
        };
        dispatch(transferCreateAddProduct(dsku, productWithoutInventory));
      }

      dispatch(getReplenishmentUnifiedInventory(dskus));
    },
    [dispatch, dskuQuantities, isKittingEnabled]
  );

  const kitDskuDetails = useSelector(getProductChooserKittedDSKUDetails);

  const removeProduct = useCallback(
    (dsku: string, selectedProduct: SelectedProductData) => {
      if (isKittingEnabled && kitDskuDetails?.[dsku]) {
        dispatch(removeKittedProduct(dsku, dskuQuantities));
      } else {
        dispatch(transferCreateRemoveProduct(dsku, selectedProduct));
      }

      const remainingDskus = dskuQuantities.filter((item) => item.dsku !== dsku).map((item) => item.dsku);
      if (!isEmpty(remainingDskus)) {
        dispatch(getReplenishmentUnifiedInventory(remainingDskus));
      }
    },
    [dispatch, dskuQuantities, isKittingEnabled, kitDskuDetails]
  );

  const selectedProductsWithMessages = useMemo(() => values(selectedProductsFromRedux), [selectedProductsFromRedux]);

  const onApplyResolution = useCallback(
    (
      originalProductDsku: string,
      issueType: InvalidEntityIssueType,
      resolutionData: any,
      originalProduct: SelectedProductData
    ) => {
      const { newProduct, unitsPerCase, numCases, quantity } = resolutionData;

      if (newProduct) {
        const newProductListItem = newProduct as ProductListItem;

        let effectiveCaseQty = newProductListItem.raw?.caseQty || 1;
        if (typeof unitsPerCase === "number" && !isNaN(unitsPerCase) && unitsPerCase > 0) {
          effectiveCaseQty = unitsPerCase;
        }

        let effectiveQty = 0;
        if (typeof quantity === "number" && !isNaN(quantity)) {
          effectiveQty = quantity;
        }

        const replacementSelectedProductData: SelectedProductData = {
          ...originalProduct,
          dsku: newProductListItem.dsku,
          name: newProductListItem.name || newProductListItem.dsku,
          caseQty: effectiveCaseQty,
          qty: effectiveQty,
          msku: newProductListItem.msku || newProductListItem.raw?.sellerSku || undefined,
          isKit: !!newProductListItem.raw?.isKit,
          objectID: newProductListItem.dsku,
          feePreview: 0,
          raw: newProductListItem.raw,
          productAliases: newProductListItem.raw?.aliases || [],
          storageOnHandQty: undefined,
          storageUnfilledOrderQty: undefined,
          storageUnavailableQty: undefined,
          onHandQty: 0,
          unfilledOrderQty: 0,
          unavailableQty: 0,
          errorMessage: undefined,
          invalidEntity: undefined,
          showProductBladeIcon: true,
        };

        removeProduct(originalProductDsku, originalProduct);
        addProduct(replacementSelectedProductData.dsku, replacementSelectedProductData);
      } else {
        // No new product selected, updating the original product using remove & add.
        const existingProduct = selectedProductsFromRedux[originalProductDsku];
        if (!existingProduct) {
          return;
        }

        let finalCaseQty = existingProduct.caseQty ?? 1;
        // If user changed unitsPerCase in the blade for the existing product and it's a valid positive number.
        if (typeof unitsPerCase === "number" && !isNaN(unitsPerCase) && unitsPerCase > 0) {
          finalCaseQty = unitsPerCase;
        }

        let finalQty = 0;
        if (shouldUseCasePack) {
          const resolvedNumCases = typeof numCases === "number" && !isNaN(numCases) ? numCases : 0;
          finalQty = finalCaseQty * resolvedNumCases;
        } else {
          finalQty = typeof quantity === "number" && !isNaN(quantity) ? quantity : 0;
        }

        const updatedSelectedProductData: SelectedProductData = {
          ...existingProduct,
          caseQty: finalCaseQty,
          qty: finalQty,
          errorMessage: undefined,
          invalidEntity: undefined,
          showProductBladeIcon: true,
        };

        removeProduct(originalProductDsku, existingProduct);
        addProduct(originalProductDsku, updatedSelectedProductData);
      }
    },
    [removeProduct, addProduct, selectedProductsFromRedux, shouldUseCasePack]
  );

  const updateQty = useCallback(
    (dsku: string, totalQuantity: number) => {
      const availableInventory = getAvailableInventory(dsku, inventoryPool, selectedProductsFromRedux);

      const validQuantity = clamp(isInteger(totalQuantity) ? totalQuantity : 0, 0, availableInventory);
      dispatch(transferCreateUpdateProductQty(dsku, validQuantity));
    },
    [dispatch, selectedProductsFromRedux, inventoryPool]
  );

  const updateNumberOfCases = useCallback(
    (dsku: string, numberOfCases: number) => {
      const product = selectedProductsFromRedux[dsku];
      const availableInventory = getAvailableInventory(dsku, inventoryPool, selectedProductsFromRedux);
      const caseQty = product?.caseQty ?? 1;

      const maxNumberOfCases = Math.floor(availableInventory / caseQty);
      const totalCases = clamp(isInteger(numberOfCases) ? numberOfCases : 0, 0, maxNumberOfCases);

      dispatch(transferCreateUpdateProductNumberOfCases(dsku, totalCases));
    },
    [dispatch, selectedProductsFromRedux, inventoryPool]
  );

  const handleEachPickingToggle = useCallback(() => {
    dispatch(transferCreateSetIsEachPickingOrderFlag(!isEachPickingOrder));
  }, [dispatch, isEachPickingOrder]);

  const selectedProductsLotNumbersList = useMemo(
    () => getSelectedProductsLotNumbersList(map(dskuQuantities, "dsku"), warehouseSelection),
    [dskuQuantities, warehouseSelection]
  );

  const updateLotNumber = useCallback(
    (dsku: string, lotNumber?: string) => {
      const lotDetails = isEmpty(lotNumber)
        ? undefined
        : selectedProductsLotNumbersList?.[dsku]?.find((lot) => lot.lotNumber === lotNumber);

      dispatch(transferCreateUpdateProductLotNumber(dsku, lotDetails));
      updateNumberOfCases(dsku, 0);
    },
    [dispatch, selectedProductsLotNumbersList, updateNumberOfCases]
  );
  const shouldShowBulkProductUploadReport = !isEmpty(selectedProducts) && !isEmpty(bulkProductUploadResult);

  return {
    addProduct,
    removeProduct,
    updateQty,
    updateNumberOfCases,
    updateLotNumber,
    handleEachPickingToggle,
    isNextDisabled: isNextStepDisabled(isInventoryLoading, selectedProductsFromRedux, warehouseSelection),
    isNextStepLoading,
    isEachPickingOrder,
    selectedProducts: selectedProductsWithMessages,
    selectedProductsLotNumbersList,
    selectedProductsLotNumbers,
    distributionChannel,
    inventoryPool,
    isKittingEnabled,
    shouldShowEachPickButton,
    shouldShowLotNumberDropdown,
    shouldUseCasePack,
    shouldShowBulkProductUploadReport,
    // For the blade
    isProductBladeOpen,
    productForBlade,
    openProductBlade,
    closeProductBlade,
    currentProductInvalidEntities,
    onApplyResolution,
  };
};
